/**
 * The case core's API client — the queues, the case, the stage control, its
 * tasks and its reviews (spec §E, ADR-0009).
 *
 * A client per module, not a growing `lib/api.ts` (waves.md §1.1).
 *
 * **No rule is re-derived here.** Whether this person may move a case into a
 * stage, whether they may decide a review, whether the case is frozen — all of
 * it is answered by the server on the detail payload (`can_move_here`,
 * `can_decide_review`, `frozen`), because the server is the thing that will
 * refuse. Two copies of a permission rule agree today and disagree in a year,
 * and the one the firm sees would be the wrong one — the shape `lib/api/
 * services.ts` set in Wave 2B for the workflow validator.
 *
 * The pure helpers at the bottom live beside the client rather than inside a
 * component so the screens' rules can be tested without rendering anything.
 */

import { api } from '../api'

// -- shapes ------------------------------------------------------------------

export type StageCategory = 'open' | 'in_progress' | 'review' | 'completed' | 'cancelled'
export type CaseSource = 'calendar' | 'request' | 'proposal' | 'manual'
export type Priority = 'low' | 'normal' | 'high' | 'urgent'
export type Queue = 'mine' | 'team' | 'all' | 'requests' | 'reviews'

export interface Case {
  id: string
  number: string
  fy: string
  title: string
  client_id: string
  service_id: string
  service_name: string | null
  plan_id: string | null
  workflow_version_id: string
  stage_key: string
  /** The firm's word for where the case is. */
  status: string
  /** What the **client** is told (ADR-0009 §3) — `null` until the case reaches
   *  a client-visible stage. The portal reads only this. */
  client_status: string | null
  category: StageCategory
  assignee_user_id: string | null
  reviewer_user_id: string | null
  branch_id: string | null
  priority: Priority
  due_date: string | null
  period: string | null
  source: CaseSource
  opened_at: string | null
  started_at: string | null
  completed_at: string | null
  fields: Record<string, unknown>
}

export interface HistoryEntry {
  id: string
  from_stage_key: string | null
  from_status: string | null
  to_stage_key: string
  to_status: string
  category: StageCategory
  client_visible: boolean
  reason: string | null
  actor_user_id: string | null
  actor_kind: string
  at: string | null
}

export interface CaseTask {
  id: string
  case_id: string
  stage_key: string | null
  title: string
  notes: string | null
  assignee_user_id: string | null
  role: string | null
  due_date: string | null
  /** The workflow's own checklist. A case cannot leave a stage with one of
   *  these open (FR-E4); a task somebody added is never required. */
  is_required: boolean
  is_done: boolean
  completed_at: string | null
  completed_by: string | null
}

export interface CaseReview {
  id: string
  case_id: string
  stage_key: string
  status: 'open' | 'approved' | 'rejected'
  reviewer_user_id: string | null
  reviewer_role: string | null
  on_approve: string | null
  on_reject: string | null
  note: string | null
  opened_at: string | null
  decided_at: string | null
  decided_by: string | null
}

export interface CaseStage {
  key: string
  name: string
  order: number
  category: StageCategory
  client_visible: boolean
  client_status: string | null
  allowed_roles: string[]
  requires_review: boolean
  on_approve: string | null
  on_reject: string | null
  required_tasks: Array<{ name: string; role: string | null }>
  actions: Array<{ kind: string; config: Record<string, string> }>
  effects: string[]
  /** Answered by the server, using the engine's own rule, so the control greys
   *  out for exactly the reason the API would refuse. */
  can_move_here: boolean
  is_current: boolean
}

export interface CaseDetail {
  case: Case
  client: { id: string; display_name: string; legal_name: string }
  service: { id: string; name: string }
  workflow: { version_id: string; version: number | null; stages: CaseStage[] }
  review: CaseReview | null
  frozen: boolean
  can_decide_review: boolean
  tasks: CaseTask[]
  history: HistoryEntry[]
}

export interface CaseVocabulary {
  queues: Queue[]
  sources: CaseSource[]
  priorities: Priority[]
  categories: StageCategory[]
  period_shapes: string[]
  can_view_all: boolean
  can_create: boolean
  can_update: boolean
}

export interface ReviewRow extends CaseReview {
  case: Case
  can_decide: boolean
}

export interface QueueFilters {
  category?: string
  client_id?: string
  service_id?: string
  assignee_user_id?: string
  priority?: string
  due_before?: string
  q?: string
  limit?: number
  offset?: number
}

// -- the client --------------------------------------------------------------

export const cases = {
  vocabulary: () => api<CaseVocabulary>('/api/v1/cases/vocabulary'),

  queue: (queue: Queue, filters: QueueFilters = {}) => {
    const params = new URLSearchParams({ queue })
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== null && value !== '') params.set(key, String(value))
    }
    return api<Case[]>(`/api/v1/cases?${params.toString()}`)
  },

  read: (id: string) => api<CaseDetail>(`/api/v1/cases/${id}`),

  open: (body: {
    client_id: string
    service_id: string
    plan_id?: string
    period?: string
    title?: string
    priority?: Priority
    due_date?: string
    assignee_user_id?: string
  }) => api<Case>('/api/v1/cases', { method: 'POST', body }),

  update: (
    id: string,
    body: Partial<{
      title: string
      priority: Priority
      due_date: string
      assignee_user_id: string
      reviewer_user_id: string
      period: string
      clear_assignee: boolean
      clear_reviewer: boolean
      clear_due_date: boolean
    }>,
  ) => api<Case>(`/api/v1/cases/${id}`, { method: 'PATCH', body }),

  /** **The stage control.** Its own route and not a PATCH: moving a case runs a
   *  role rule, a task gate, a freeze, the category's effects and the stage's
   *  automation, and a PATCH that quietly did all that is a PATCH somebody will
   *  write a script against. */
  move: (id: string, to_stage: string, reason?: string) =>
    api<{
      case: Case
      moved_to: string
      effects: string[]
      review_opened: string | null
      tasks_created: CaseTask[]
      actions_queued: string[]
    }>(`/api/v1/cases/${id}/move`, { method: 'POST', body: { to_stage, reason } }),

  history: (id: string) => api<HistoryEntry[]>(`/api/v1/cases/${id}/history`),
  tasks: (id: string) => api<CaseTask[]>(`/api/v1/cases/${id}/tasks`),
  addTask: (
    id: string,
    body: { title: string; notes?: string; due_date?: string; stage_key?: string },
  ) => api<CaseTask>(`/api/v1/cases/${id}/tasks`, { method: 'POST', body }),
  setTask: (
    taskId: string,
    body: Partial<{ title: string; notes: string; due_date: string; is_done: boolean }>,
  ) => api<CaseTask>(`/api/v1/cases/tasks/${taskId}`, { method: 'PATCH', body }),

  reviews: (status: 'open' | 'approved' | 'rejected' = 'open') =>
    api<ReviewRow[]>(`/api/v1/cases/reviews?status=${status}`),
  decide: (reviewId: string, approve: boolean, note?: string) =>
    api<{ review: CaseReview; case: Case }>(`/api/v1/cases/reviews/${reviewId}/decide`, {
      method: 'POST',
      body: { approve, note },
    }),
}

// -- pure helpers, so the screens' rules are testable ------------------------

export const CATEGORY_LABELS: Record<StageCategory, string> = {
  open: 'Open',
  in_progress: 'In progress',
  review: 'In review',
  completed: 'Completed',
  cancelled: 'Cancelled',
}

export const QUEUE_LABELS: Record<Queue, string> = {
  mine: 'Mine',
  team: 'Team',
  all: 'All',
  requests: 'Requests',
  reviews: 'Reviews',
}

export const PRIORITY_LABELS: Record<Priority, string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  urgent: 'Urgent',
}

/**
 * What a case is *called* on a list row.
 *
 * The server composes it from the service and the period when the firm gave
 * none, and never stores it — a denormalised title goes stale the first time the
 * service is renamed. This is the fallback for a payload that predates that.
 */
export function caseLabel(row: Pick<Case, 'title' | 'service_name' | 'period'>): string {
  if (row.title) return row.title
  const name = row.service_name ?? 'Case'
  return row.period ? `${name} · ${row.period}` : name
}

/**
 * The two statuses, as a row should show them (ADR-0009 §3).
 *
 * The firm's word always; the client's beside it **only when they differ**.
 * Showing "In preparation / In preparation" twice teaches a reader that the two
 * columns are the same thing, which is the misunderstanding the second column
 * exists to prevent.
 */
export function statusPair(row: Pick<Case, 'status' | 'client_status'>): {
  firm: string
  client: string | null
} {
  const client = row.client_status && row.client_status !== row.status ? row.client_status : null
  return { firm: row.status, client }
}

/** How a due date reads on a row: overdue, today, or the date. */
export function dueLabel(due: string | null, today = new Date()): {
  text: string
  tone: 'overdue' | 'today' | 'soon' | 'none'
} {
  if (!due) return { text: '—', tone: 'none' }
  const at = new Date(`${due}T00:00:00`)
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  const days = Math.round((at.getTime() - start.getTime()) / 86_400_000)
  if (days < 0) return { text: `${-days}d overdue`, tone: 'overdue' }
  if (days === 0) return { text: 'Today', tone: 'today' }
  if (days <= 7) return { text: `In ${days}d`, tone: 'soon' }
  return { text: at.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }), tone: 'none' }
}

/**
 * Whether the stage control can offer anything at all, and the sentence for
 * why it cannot.
 *
 * **The freeze is checked before the verb**, which is the same order the engine
 * uses and for the same reason: telling a partner "you do not have permission"
 * when the truth is "this case is with the reviewer" sends them to support with
 * the wrong question.
 */
export function stageControl(detail: CaseDetail | null): {
  enabled: boolean
  reason: string | null
  options: CaseStage[]
} {
  if (!detail) return { enabled: false, reason: null, options: [] }
  if (detail.frozen) {
    return {
      enabled: false,
      reason:
        'This case is with the reviewer. It cannot be moved until the review is approved or sent back — that freeze applies to everyone, including a partner.',
      options: [],
    }
  }
  const options = detail.workflow.stages.filter((s) => !s.is_current)
  if (!options.some((s) => s.can_move_here)) {
    return {
      enabled: false,
      reason:
        'Your firm has not given your role permission to move this case into any of the remaining stages.',
      options,
    }
  }
  return { enabled: true, reason: null, options }
}

/**
 * The required tasks still open on the case's current stage — what blocks it
 * from leaving (FR-E4).
 *
 * The screen names them rather than greying the control with no explanation:
 * the whole content of that refusal is *which* items are outstanding.
 */
export function blockingTasks(detail: CaseDetail | null): CaseTask[] {
  if (!detail) return []
  return detail.tasks.filter(
    (t) => t.is_required && !t.is_done && t.stage_key === detail.case.stage_key,
  )
}

/** One history row as a sentence. `null` on the left is the opening — a case
 *  did not come from anywhere, and rendering "— → Received" says nothing. */
export function historyLine(entry: HistoryEntry): string {
  if (!entry.from_status) return `Opened at ${entry.to_status}`
  return `${entry.from_status} → ${entry.to_status}`
}

// ===========================================================================
// w4a — what happens around a case (spec §C5, §E5–E12, §L2–L3)
// ===========================================================================

export interface Candidate {
  user_id: string
  open_cases: number
  open_on_service: number
  last_assigned_at: string | null
}

export interface ServiceRequest {
  id: string
  client_id: string | null
  person_id: string | null
  service_id: string
  service_name?: string | null
  plan_id: string | null
  source: 'site' | 'portal'
  contact_name: string
  contact_email: string | null
  contact_mobile: string | null
  entity_details: Record<string, unknown>
  message: string | null
  status: 'new' | 'accepted' | 'declined'
  case_id: string | null
  decline_reason: string | null
  received_at: string | null
  decided_at: string | null
  decided_by: string | null
}

export interface CaseChange {
  id: string
  case_id: string
  kind: 'plan' | 'service'
  from_id: string | null
  to_id: string
  reason: string
  status: 'proposed' | 'approved' | 'rejected'
  approver_role: string | null
  proposed_by: string | null
  proposed_at: string | null
  decided_at: string | null
  decided_by: string | null
  decision_note: string | null
  /** Answered by the server, using the firm's configured approver and the
   *  "not your own" rule. Never re-derived here. */
  can_decide?: boolean
}

export type CallbackStatus = 'scheduled' | 'done' | 'missed' | 'cancelled'

export interface Callback {
  id: string
  subject_kind: string
  subject_id: string
  subject_label: string | null
  client_id: string | null
  scheduled_at: string
  assignee_user_id: string | null
  note: string | null
  status: CallbackStatus
  outcome: string | null
  /** **Late is not missed** (FR-E10). Answered by the server: `missed` is
   *  somebody saying nobody picked up, this is the clock. */
  is_late: boolean
  completed_at: string | null
  completed_by: string | null
}

export interface TimeEntry {
  id: string
  case_id: string
  client_id: string
  user_id: string
  spent_on: string
  minutes: number
  note: string | null
  /** Rupees an hour, or `null` when the firm does not price by the hour.
   *  `null` is not zero — see `app/api/cases.py: _rate_paise`. */
  rate: number | null
}

export interface TimelineEntry {
  kind: string
  id: string
  at: string
  title: string
  detail: string | null
  actor_user_id: string | null
  actor_kind: string
  meta: Record<string, unknown>
}

export interface ClientPanels {
  cases: Case[]
  /** `null` — not `[]` — when the caller holds no `calendar:can_view`. An
   *  empty list would render as "this client has nothing due", which is a
   *  different and false statement. */
  due_dates:
    | Array<{
        id: string
        item_name: string
        period: string
        due_date: string
        status: string
        case_id: string | null
      }>
    | null
}

export const caseFlows = {
  candidates: (params: { case_id?: string; branch_id?: string; service_id?: string } = {}) => {
    const search = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) if (value) search.set(key, value)
    return api<Candidate[]>(`/api/v1/cases/candidates?${search.toString()}`)
  },

  assign: (
    caseId: string,
    body: {
      assignee_user_id?: string
      reviewer_user_id?: string
      round_robin?: boolean
      clear_assignee?: boolean
      clear_reviewer?: boolean
    },
  ) =>
    api<{ case: Case; picked: Candidate | null }>(`/api/v1/cases/${caseId}/assign`, {
      method: 'POST',
      body,
    }),

  requests: (status: 'new' | 'accepted' | 'declined' = 'new') =>
    api<ServiceRequest[]>(`/api/v1/cases/requests?status=${status}`),
  acceptRequest: (
    requestId: string,
    body: { client_id?: string; plan_id?: string; assignee_user_id?: string; title?: string },
  ) =>
    api<{ request: ServiceRequest; case: Case }>(
      `/api/v1/cases/requests/${requestId}/accept`,
      { method: 'POST', body },
    ),
  declineRequest: (requestId: string, reason: string) =>
    api<ServiceRequest>(`/api/v1/cases/requests/${requestId}/decline`, {
      method: 'POST',
      body: { reason },
    }),

  changes: (caseId: string) => api<CaseChange[]>(`/api/v1/cases/${caseId}/changes`),
  proposeChange: (caseId: string, body: { kind: 'plan' | 'service'; to_id: string; reason: string }) =>
    api<CaseChange>(`/api/v1/cases/${caseId}/changes`, { method: 'POST', body }),
  decideChange: (changeId: string, approve: boolean, note?: string) =>
    api<{ change: CaseChange; case: Case; workflow_version_unchanged: boolean }>(
      `/api/v1/cases/changes/${changeId}/decide`,
      { method: 'POST', body: { approve, note } },
    ),

  callbacks: (caseId: string) => api<Callback[]>(`/api/v1/cases/${caseId}/callbacks`),
  scheduleCallback: (
    caseId: string,
    body: { scheduled_at: string; assignee_user_id?: string; note?: string },
  ) => api<Callback>(`/api/v1/cases/${caseId}/callbacks`, { method: 'POST', body }),
  updateCallback: (
    callbackId: string,
    body: Partial<{ status: 'done' | 'missed' | 'cancelled'; outcome: string; scheduled_at: string }>,
  ) => api<Callback>(`/api/v1/cases/callbacks/${callbackId}`, { method: 'PATCH', body }),

  time: (caseId: string) => api<TimeEntry[]>(`/api/v1/cases/${caseId}/time`),
  logTime: (caseId: string, body: { minutes: number; spent_on?: string; note?: string; rate?: number }) =>
    api<TimeEntry>(`/api/v1/cases/${caseId}/time`, { method: 'POST', body }),
  week: (from: string, to: string, mine = true) =>
    api<TimeEntry[]>(`/api/v1/cases/time?from=${from}&to=${to}&mine=${mine}`),

  timeline: (caseId: string, kinds?: string[]) =>
    api<TimelineEntry[]>(
      `/api/v1/cases/${caseId}/timeline${kinds?.length ? `?kinds=${kinds.join(',')}` : ''}`,
    ),

  clientPanels: (clientId: string) => api<ClientPanels>(`/api/v1/cases/client/${clientId}`),
}

// -- pure helpers ------------------------------------------------------------

/**
 * Which queue tabs this person gets.
 *
 * **Team is dropped, not shown empty, without `can_view_all`** — the same rule
 * the sidebar applies to a nav entry: an empty tab advertises a capability the
 * user cannot obtain and generates a support ticket. An associate's book is
 * their own work, and *Mine* is already that.
 *
 * **Requests** was dropped unconditionally by Wave 3A, which had nothing behind
 * it. w4a filled the inbox and the rule became the real one: the API gates it
 * on `case:can_create`, because everything a person does on that screen ends in
 * a case opening — so it is dropped for the article assistant and the viewer,
 * who hold no `can_create`. `vocabulary.can_create` is the server's own answer
 * to that question and is not re-derived here.
 */
export function visibleQueues(vocab: CaseVocabulary | null): Queue[] {
  if (!vocab) return ['mine']
  return vocab.queues.filter((queue) => {
    if (queue === 'team' || queue === 'all') return vocab.can_view_all
    if (queue === 'requests') return vocab.can_create
    return true
  })
}

/**
 * How a call back reads on a row.
 *
 * **Late and missed are different things** and this is the only place the
 * difference is turned into words. `is_late` comes from the server (the clock);
 * `missed` is somebody having said nobody picked up. A screen that collapsed
 * the two would tell a firm it had tried when it had not.
 */
export function callbackLabel(row: Pick<Callback, 'status' | 'is_late' | 'scheduled_at'>): {
  text: string
  tone: 'overdue' | 'today' | 'soon' | 'none'
} {
  if (row.status === 'done') return { text: 'Called back', tone: 'none' }
  if (row.status === 'missed') return { text: 'No answer', tone: 'overdue' }
  if (row.status === 'cancelled') return { text: 'Called off', tone: 'none' }
  if (row.is_late) return { text: 'Late', tone: 'overdue' }
  return { text: whenLabel(row.scheduled_at), tone: 'soon' }
}

/** A call-back time, in the reader's own zone. */
export function whenLabel(at: string): string {
  const when = new Date(at)
  return when.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/** Minutes as a practice writes them: `1h 30m`, `45m`, `2h`. */
export function hoursLabel(minutes: number): string {
  if (minutes <= 0) return '0m'
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  if (!hours) return `${rest}m`
  return rest ? `${hours}h ${rest}m` : `${hours}h`
}

/**
 * What a request is *called* in the inbox, and whether staff have to pick a
 * client before they can accept it.
 *
 * A request from the public form names an entity that has no record. The API
 * refuses to accept without a client and hands back what was typed; this is the
 * screen's half of that, so the dialog opens on the right step instead of
 * showing an error after the fact.
 */
export function requestNeedsClient(row: Pick<ServiceRequest, 'client_id'>): boolean {
  return !row.client_id
}

/** The entity the requester described, as one line. */
export function entityLine(row: Pick<ServiceRequest, 'entity_details'>): string | null {
  const details = row.entity_details ?? {}
  const name = typeof details.legal_name === 'string' ? details.legal_name : null
  if (!name) return null
  const kind = typeof details.kind === 'string' ? details.kind : null
  return kind ? `${name} · ${kind}` : name
}

/**
 * Whether the stage control should also offer *"propose a change"*, and why
 * not when it should not.
 *
 * The freeze is the same one the stage control obeys, and it is checked here
 * for the same reason: re-pricing work somebody is in the middle of checking is
 * how the two come apart.
 */
export function changeControl(detail: CaseDetail | null): { enabled: boolean; reason: string | null } {
  if (!detail) return { enabled: false, reason: null }
  if (detail.frozen) {
    return {
      enabled: false,
      reason: 'This case is with the reviewer. A change of plan or service can be proposed once the review is decided.',
    }
  }
  return { enabled: true, reason: null }
}
